import { useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { listActiveDocuments } from '../api/documents';
import type { ActiveDocument } from '../api/documents';
import { loadSettings } from '../lib/settings';
import { documentAlerts } from '../domain/documents';
import type { DocumentAlert } from '../domain/documents';

const TZ = 'America/Lima';

/**
 * Las alertas de documentos laborales por vencer o vencidos, de toda la gente visible.
 *
 * Vivía dentro del tablero de **Hoy** y el usuario la movió: *«estas alertas deberían estar en la
 * sección "Trabajadores"»*. Tiene razón y la razón es de encuadre, no de gusto: Hoy responde
 * «¿cómo va la operación de hoy?» —quién marcó, quién falta, qué alertas de GPS hay— y un carnet
 * de sanidad que vence en 9 días no se resuelve hoy ni en esa pantalla. Se resuelve llamando a la
 * persona, que es lo que se hace desde Trabajadores.
 *
 * Es un componente y no un bloque copiado porque la lista ya existía en Hoy con su propia carga de
 * datos. Copiarla habría dejado dos implementaciones del mismo aviso, y ya hay lección de eso en
 * este proyecto (§10: el diccionario de banderas duplicado en tres pantallas).
 *
 * Carga sus propios datos a propósito: así se puede poner en cualquier pantalla sin que la pantalla
 * tenga que saber de documentos. Si la consulta falla, el componente **no se dibuja** en lugar de
 * mostrar un error: es un aviso complementario, y romper la pantalla de Trabajadores porque no se
 * pudieron leer los vencimientos sería cambiar un problema chico por uno grande.
 */
export default function AlertasDeDocumentos({ limite = 5 }: { limite?: number }) {
  const [alertas, setAlertas] = useState<DocumentAlert[]>([]);
  const [nombres, setNombres] = useState<Record<string, string>>({});

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const [docs, settings] = await Promise.all([listActiveDocuments(), loadSettings()]);
        if (!vivo) return;
        const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
        setAlertas(documentAlerts(docs, hoy, settings.docWarnDays));
        setNombres(Object.fromEntries(
          (docs as ActiveDocument[]).map((d) => [d.workerId, d.workerName]),
        ));
      } catch {
        // Silencio deliberado: ver el comentario del componente.
      }
    })();
    return () => { vivo = false; };
  }, []);

  if (alertas.length === 0) return null;

  const vencidos = alertas.filter((a) => a.severity === 'vencido').length;

  return (
    <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-amber-900">Documentos laborales</p>
        {/* Cuántos hay en total y cuántos ya vencieron: son dos urgencias distintas y la lista
            recortada a cinco no alcanza para verlo. */}
        <p className="text-xs text-amber-800">
          {alertas.length} por atender{vencidos > 0 ? ` · ${vencidos} ya vencidos` : ''}
        </p>
      </div>
      <ul className="space-y-0.5 text-sm text-amber-900">
        {alertas.slice(0, limite).map((a) => (
          <li key={`${a.workerId}-${a.type}-${a.daysLeft}`}>
            <span className="font-medium">{nombres[a.workerId] ?? a.workerId}</span>: {a.message}
          </li>
        ))}
      </ul>
      {alertas.length > limite && (
        <p className="mt-1 text-xs text-amber-800">y {alertas.length - limite} más…</p>
      )}
    </div>
  );
}
